<script setup lang="ts">
import { computed } from 'vue'

import { MultiSelect, TextInput } from '@collector/shared-ui'
import {
  EventDetailId,
  SportDetailInputType,
  SportDetailPossibleValue,
  SportDetails,
} from '@collector/sportsapi-client-legacy'
import { sportsApiClient } from '@desktop/globalState/sportsApiClient'
import {
  useEvent,
  useEventDetails,
  useSportMaps,
} from '@desktop/views/Relation/composables'

import { injectSuccessBadge } from '../../../successBadge'
import { eventDetailSelectDisableRules } from './event-detail.consts'
import {
  getEventDetailSelectOptions,
  isSelectOptionDisabled,
} from './event-detail.utils'

interface Props {
  eventDetailId: EventDetailId
  postfix?: 'min' | 's'
  maxlength?: number
  wide?: boolean
  label?: string
  inputClass?: string
}

const props = defineProps<Props>()

// inject
const { showSuccessBadge } = injectSuccessBadge()

// composables
const { eventDetailIdToSportDetailsMap } = useSportMaps()
const { event } = useEvent()
const { getEventDetailValue, setEventDetailValue } = useEventDetails()

// data
const sportDetail = eventDetailIdToSportDetailsMap.value[props.eventDetailId]
if (!sportDetail) {
  throw new Error(`Sport detail not found for id: ${props.eventDetailId}`)
}

const selectOptions = computed(() =>
  getEventDetailSelectOptions(
    sportDetail.id,
    getEventDetailValue(sportDetail.id),
    sportDetail.possible_values,
    eventDetailSelectDisableRules,
  ),
)

function handleSelect(value: unknown): void {
  if (typeof value !== 'string') {
    return
  }

  const option = sportDetail.possible_values.find(
    (possibleValue) => possibleValue.value === value,
  )

  if (
    option &&
    isSelectOptionDisabled(
      eventDetailSelectDisableRules,
      sportDetail.id,
      getEventDetailValue(sportDetail.id),
      option,
      sportDetail.possible_values,
    )
  ) {
    return
  }

  void updateValue(
    sportDetail.id,
    getSportDetailPossibleValueKey(sportDetail, value) || '',
  )
}

async function handleBlur(
  detailId: EventDetailId,
  value: string,
): Promise<void> {
  if (getEventDetailValue(detailId) !== value) {
    await updateValue(detailId, value)
  }
}

function getSportDetailPossibleValueKey(
  detail: SportDetails,
  value: string,
): SportDetailPossibleValue['key'] {
  return detail.possible_values.find(
    (possibleValue) => possibleValue.value === value,
  )?.key as number
}

async function updateValue(
  detailId: EventDetailId,
  value: string | number,
): Promise<void> {
  setEventDetailValue(detailId, value)

  await sportsApiClient.Events.patch(event.value.id, {
    details: { [detailId]: value },
  })

  showSuccessBadge()
}
</script>

<template>
  <div>
    <TextInput
      v-if="sportDetail.input === SportDetailInputType.Number"
      inputClass="w-18"
      :modelValue="getEventDetailValue(sportDetail.id) as string"
      :postfix
      :maxlength
      numeric
      @blur="handleBlur(sportDetail.id, $event.value)"
    >
      <span
        :class="{
          'w-12': !wide,
          'w-16': wide,
        }"
      >
        {{ label || sportDetail.name }}
      </span>
    </TextInput>
    <MultiSelect
      v-else-if="sportDetail.input === SportDetailInputType.Select"
      :options="selectOptions"
      :modelValue="getEventDetailValue(sportDetail.id) as string"
      :inputClass="inputClass || 'w-full'"
      @update:modelValue="handleSelect"
    >
      <slot>
        <span
          :class="{
            'w-12': !wide,
            'w-16': wide,
          }"
        >
          {{ label || sportDetail.name }}
        </span>
      </slot>
    </MultiSelect>
  </div>
</template>
